import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { FounderNav } from '../components/FounderNav';
import { sendFounderTestEmail, type DeliverabilityTestResult } from '../lib/api';
import { alertDialog } from '../lib/dialog';

/**
 * Pre-launch diagnostics — one founder surface for "is the plumbing
 * actually working." Two tools today:
 *
 *   1. Magic-link deliverability tester. Sends a real magic-link
 *      shaped email (with a tombstone token) to any address. Verifies
 *      Resend accepts the request, the from-domain is verified, and
 *      — once you check that inbox — the email lands in the primary
 *      tab. Critical to test against a real .edu before opening
 *      sign-ups to a school.
 *
 *   2. Sentry capture verification. One button throws a JS error
 *      inside this page (caught by Sentry.ErrorBoundary at the React
 *      root), another hits a server endpoint that throws (caught by
 *      Hono onError + Sentry.captureException on the api). Both
 *      should appear at sentry.io within seconds if the DSN is wired
 *      correctly and the source maps were uploaded on the deploy.
 *
 * No automated assertion — these are eyeball checks. The page just
 * exposes the actions and tells the operator where to look.
 */

const TEST_DOMAINS = [
  { label: 'Gmail (your inbox)', value: 'luis.gomez6448@gmail.com' },
  { label: 'Add a different address', value: '' },
];

export function FounderDiagnostics() {
  const [email, setEmail] = useState(TEST_DOMAINS[0]?.value ?? '');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<DeliverabilityTestResult | null>(null);
  const [history, setHistory] = useState<
    Array<{ to: string; ok: boolean; at: string; error?: string }>
  >([]);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (sending || !email.trim()) return;
    setSending(true);
    setLastResult(null);
    const result = await sendFounderTestEmail(email.trim());
    setSending(false);
    setLastResult(result);
    setHistory((prev) =>
      [
        {
          to: email.trim(),
          ok: result.ok,
          at: new Date().toISOString(),
          error: result.error,
        },
        ...prev,
      ].slice(0, 10),
    );
  }

  /** Throw inside an effect-free render path so Sentry.ErrorBoundary
   *  catches it cleanly. setState + throw in the next render. */
  const [clientErrorRequested, setClientErrorRequested] = useState(false);
  if (clientErrorRequested) {
    throw new Error('Founder-triggered client-side Sentry test error — this is intentional.');
  }

  async function triggerServerError() {
    const res = await fetch('/api/founder/test-error', { credentials: 'include' });
    await alertDialog({
      title: 'Server error triggered',
      message: `The API returned ${res.status}. Open your Sentry dashboard at https://lessalone.sentry.io to confirm the event landed — it usually appears within 5–10 seconds.`,
      confirmLabel: 'OK',
    });
  }

  return (
    <main className="wide">
      <div className="card">
        <FounderNav />
        <h1>Diagnostics</h1>
        <p className="muted">
          Pre-launch checks. Use these to verify email delivery + error tracking are wired before
          opening sign-ups to a real cohort.
        </p>

        <section className="user-section">
          <h2>Magic-link deliverability</h2>
          <p className="muted">
            Sends a real sign-in-shaped email to any address (the token is a tombstone — the
            recipient can't actually sign in). Use this against a Gmail inbox, an Outlook inbox, a
            university .edu — anywhere a student or moderator might receive a sign-in link.
          </p>
          <form onSubmit={send} className="diag-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@university.edu"
              required
              className="diag-input"
            />
            <button type="submit" disabled={sending || !email.trim()}>
              {sending ? 'Sending…' : 'Send test email'}
            </button>
          </form>
          {lastResult && (
            <div
              className={`diag-result ${lastResult.ok ? 'diag-result--ok' : 'diag-result--err'}`}
            >
              {lastResult.ok ? (
                <>
                  <strong>✓ Sent.</strong> Open <code>{lastResult.to}</code> and confirm the email
                  arrived (check spam too). From-domain: <code>{lastResult.fromDomain ?? '—'}</code>
                  {lastResult.resendKeyConfigured === false && (
                    <>
                      {' '}
                      <span className="muted">
                        · ⚠️ RESEND_API_KEY isn't set — email was logged to console only, not
                        actually delivered.
                      </span>
                    </>
                  )}
                </>
              ) : (
                <>
                  <strong>✗ Failed.</strong> {lastResult.error ?? 'Unknown error.'} Check the
                  RESEND_API_KEY env var and the from-domain's Resend verification status.
                </>
              )}
            </div>
          )}
          {history.length > 0 && (
            <details className="diag-history">
              <summary className="muted">Recent test sends ({history.length})</summary>
              <ul>
                {history.map((h, i) => (
                  <li key={i}>
                    <span className={h.ok ? 'tone-green' : 'tone-clay'}>{h.ok ? '✓' : '✗'}</span>{' '}
                    <code>{h.to}</code>{' '}
                    <span className="muted">· {new Date(h.at).toLocaleTimeString()}</span>
                    {h.error && <span className="muted"> · {h.error}</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>

        <section className="user-section">
          <h2>Sentry capture</h2>
          <p className="muted">
            Verify Sentry receives errors in production. Both buttons trigger intentional crashes —
            the first inside this React page (caught by the root ErrorBoundary), the second on the
            API server (caught by the global Hono error handler). After clicking, open{' '}
            <a href="https://lessalone.sentry.io/issues/" target="_blank" rel="noreferrer">
              the Sentry issues page
            </a>{' '}
            and confirm the event appears within ~10 seconds.
          </p>
          <div className="diag-actions">
            <button
              type="button"
              className="secondary danger"
              onClick={() => setClientErrorRequested(true)}
            >
              Throw client-side error
            </button>
            <button
              type="button"
              className="secondary danger"
              onClick={() => void triggerServerError()}
            >
              Throw server-side error
            </button>
          </div>
          <p className="muted note">
            The client button intentionally unmounts this page — Sentry's ErrorBoundary takes over
            and shows the brand-coloured crash fallback. To come back, reload from the home page.
          </p>
        </section>
      </div>
    </main>
  );
}
